import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Grid,
} from "@mui/material";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import { uniqueId } from "lodash";

import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";
import Backdrop from "Components/Common/Backdrop";
import SelectField from "Components/Common/SelectField";
import { applicationTypes } from "common/constant";
import { accessoryType, getOptionValue } from "common/enums";

import { onlyNumberKeys } from "utils";

import { useLazyAccessoriesQuery } from "apis/Accessories";
import {
  useGetDemandEstimationByApplicationIdQuery,
  usePostSettlementChargeMutation,
} from "apis/DemandEstimation";
import { useLazyTechnicalSanctionQuery } from "apis/TechnicalSanction";

const SettlementChargeForm = ({
  t,
  applicationData: {
    id,
    demandEstimateId,
    utilities,
    applicationType,
    utilityAddress,
  },
  handleClose,
}) => {
  const [addSettlementCharge, { isLoading }] =
    usePostSettlementChargeMutation();

  const [
    getAccessoriesData,
    {
      isLoading: isAccessoriesLoading,
      data: { data: accessoriesData = [] } = {},
    },
  ] = useLazyAccessoriesQuery();

  const [
    getTechnicalSanctionDataByApplicationId,
    {
      isLoading: isTechnicalSanctionDataLoading,
      isFetching: isTechnicalSanctionDataFetching,
      data: { data: technicalSanctionData } = [],
    },
  ] = useLazyTechnicalSanctionQuery();

  const {
    data: { data: { charges } = {} } = {},
    isLoading: isDemandEstimationDataLoading,
  } = useGetDemandEstimationByApplicationIdQuery(id);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          await getTechnicalSanctionDataByApplicationId(id);
        }
      } catch (error) {}
    })();
  }, [id]);

  const initialValuesOfAccessories = (data) => {
    if (data.length) {
      return data.map(({ id, accessories, type, quantity }) => ({
        id,
        accessoryId: accessories.id,
        type,
        quantity,
      }));
    } else {
      return [
        {
          id: uniqueId("temp_"),
          accessoryId: 0,
          type: "",
          quantity: 1,
        },
      ];
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      applicationId: id,
      accessoriesList: initialValuesOfAccessories(
        technicalSanctionData?.accessories || []
      ),
    },
    validationSchema: Yup.object({
      accessoriesList: Yup.array().of(
        Yup.object().shape({
          accessoryId: Yup.number().min(1, "Please select accessory"),
          type: Yup.string()
            .nullable()
            .required("Please select accessory type"),
          quantity: Yup.number()
            .required("Quantity is required")
            .test("isValidCapacity", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (current.quantity === 0) {
                return this.createError({
                  message: "Quantity must be greater than 0",
                });
              }
              return true;
            }),
        })
      ),
    }),
    onSubmit: async ({ accessoriesList }) => {
      try {
        const payload = accessoriesList.map((item) => {
          if (item.id.toString().startsWith("temp")) {
            return {
              ...item,
              id: 0,
            };
          }
          return item;
        });
        await addSettlementCharge(demandEstimateId, payload);
      } catch (error) {
        console.error(error);
      }
      handleClose();
    },
  });

  const {
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    values: { accessoriesList },
  } = formik;

  //add more accessories when click on Add accessories
  const handleAddAccessories = (existingAccessories) => {
    const addNewAccessories = {
      id: uniqueId("temp_"),
      type: "",
      accessoryId: 0,
      quantity: 1,
    };
    setFieldValue("accessoriesList", [
      ...existingAccessories,
      addNewAccessories,
    ]);
  };

  //increase quantity
  const handleIncreaseQuantity = (index) => {
    setFieldValue(
      `accessoriesList[${index}].quantity`,
      +accessoriesList[index].quantity + 1
    );
  };

  //decrease quantity
  const handleDecreaseQuantity = (index) => {
    setFieldValue(
      `accessoriesList[${index}].quantity`,
      accessoriesList[index].quantity > 1
        ? +accessoriesList[index].quantity - 1
        : 1
    );
  };

  //Remove accessories when click on Remove
  const handleRemoveClick = (i) => {
    const accessoryLength = accessoriesList.length || 0;
    if (accessoryLength > 1) {
      setFieldValue(
        "accessoriesList",
        accessoriesList.filter((_, index) => index !== i)
      );
      setFieldTouched(`accessoriesList[${i}]`, false, false);
    }
  };

  const isDcsUtility = useMemo(
    () => utilities[0] === getOptionValue("utilityOptions", "DCS"),
    [utilities[0]]
  );

  //calling api for getting accessories data and Sending payload if utility is DCS
  useEffect(() => {
    (async () => {
      try {
        const utilityId = isDcsUtility ? utilities[0] : "";

        const isDTA = !isDcsUtility
          ? utilityAddress?.premiseArea?.id ===
            getOptionValue("premiseAreaEnum", "DTA")
          : "";

        const isTemporaryConnection = !isDcsUtility
          ? applicationType?.id === applicationTypes["Temporary Connection"]
          : "";

        await getAccessoriesData({
          utilityId: utilityId,
          isDTA,
          isTemporaryConnection,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [utilities, applicationType, utilityAddress, isDcsUtility]);

  if (
    isLoading ||
    isDemandEstimationDataLoading ||
    isTechnicalSanctionDataLoading ||
    isTechnicalSanctionDataFetching ||
    isAccessoriesLoading
  ) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title={t("Add Settlement Charges")}
      maxWidth="md"
    >
      <Grid container className="mt-3" columnSpacing={2}>
        {charges &&
          Object.entries(charges).map(([key, value]) => (
            <>
              {value > 0 && (
                <>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      label={t(`${key}`) + "*"}
                      placeholder={`Enter ${key}`}
                      value={value}
                      disabled
                    />
                  </Grid>
                </>
              )}
            </>
          ))}
      </Grid>
      {accessoriesList.length &&
        accessoriesList.map((data, index) => (
          <Grid
            key={data.id}
            container
            columnSpacing={{ xs: 1 }}
            className="border border-2 ps-2 pb-0 p-3 mb-2"
          >
            <Grid item xs={12} sm={6} md={4}>
              <SelectField
                label={t("Accessories") + "*"}
                name={`accessoriesList.${index}.accessoryId`}
                formik={formik}
                options={accessoriesData || []}
                getOptionLabel={(option) => option?.name}
                getOptionValue={(option) => option?.id}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SelectField
                label={t("Type") + "*"}
                name={`accessoriesList[${index}].type`}
                formik={formik}
                options={accessoryType || []}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ButtonGroup
                size="small"
                aria-label="small outlined button group"
              >
                <Button
                  variant="contained"
                  sx={{ minWidth: "40px", height: "40px" }}
                  disabled={accessoriesList[index].quantity <= 1}
                  onClick={() => handleDecreaseQuantity(index)}
                >
                  -
                </Button>
                <Box sx={{ width: "110px" }}>
                  <InputFieldText
                    label={t("Quantity")}
                    name={`accessoriesList[${index}].quantity`}
                    formik={formik}
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  sx={{ minWidth: "40px", height: "40px" }}
                  onClick={() => handleIncreaseQuantity(index)}
                >
                  +
                </Button>
              </ButtonGroup>
            </Grid>

            {accessoriesList.length > 1 && (
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  color="error"
                  size="small"
                  sx={{
                    minWidth: "unset",
                    border: "1px solid",
                    height: "40px",
                    width: "100px",
                    mb: 2,
                  }}
                  onClick={() => handleRemoveClick(index)}
                >
                  <strong className="d-flex justify-content-center">
                    <DeleteOutlineSharpIcon fontSize="small" />
                    {t("Remove")}
                  </strong>
                </Button>
              </Grid>
            )}
          </Grid>
        ))}
      <Box className="d-flex justify-content-between">
        <Button
          color="primary"
          size="small"
          sx={{ minWidth: "unset", textTransform: "capitalize" }}
          variant="outlined"
          onClick={() => handleAddAccessories(accessoriesList)}
        >
          <strong>
            <span>+</span> {t("Add Accessories")}
          </strong>
        </Button>
        <Grid className="d-block text-end" mt={1}>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            onClick={handleSubmit}
          >
            Submit
            {isLoading && <CircularProgress size={20} />}
          </Button>
        </Grid>
      </Box>
    </Modal>
  );
};

SettlementChargeForm.propTypes = {
  applicationData: PropTypes.object,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(SettlementChargeForm);
